"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContentDeliveryManager = exports.streamPartIdToDataKey = void 0;
const dht_1 = require("@streamr/dht");
const utils_1 = require("@streamr/utils");
const crypto_1 = require("crypto");
const eventemitter3_1 = require("eventemitter3");
const sampleSize_1 = __importDefault(require("lodash/sampleSize"));
const PeerDescriptorStoreManager_1 = require("./PeerDescriptorStoreManager");
const StreamPartNetworkSplitAvoidance_1 = require("./StreamPartNetworkSplitAvoidance");
const StreamPartReconnect_1 = require("./StreamPartReconnect");
const createContentDeliveryLayerNode_1 = require("./createContentDeliveryLayerNode");
const ProxyClient_1 = require("./proxy/ProxyClient");
const Propagation_1 = require("./propagation/Propagation");
const logger = new utils_1.Logger(module);
const streamPartIdToDataKey = (streamPartId) => {
    return (0, dht_1.toDhtAddress)(new Uint8Array(((0, crypto_1.createHash)('sha1').update(streamPartId).digest())));
};
exports.streamPartIdToDataKey = streamPartIdToDataKey;
class ContentDeliveryManager extends eventemitter3_1.EventEmitter {
    transport;
    connectionLocker;
    controlLayerNode;
    metricsContext;
    metrics;
    options;
    streamParts;
    knownStreamPartEntryPoints = new Map();
    started = false;
    destroyed = false;
    constructor(options) {
        super();
        this.options = options;
        this.streamParts = new Map();
        this.metricsContext = options.metricsContext ?? new utils_1.MetricsContext();
        this.metrics = {
            broadcastMessagesPerSecond: new utils_1.RateMetric(),
            broadcastBytesPerSecond: new utils_1.RateMetric()
        };
        this.metricsContext.addMetrics('node', this.metrics);
    }
    async start(startedAndJoinedControlLayerNode, transport, connectionLocker) {
        if (this.started || this.destroyed) {
            return;
        }
        this.started = true;
        this.controlLayerNode = startedAndJoinedControlLayerNode;
        this.transport = transport;
        this.connectionLocker = connectionLocker;
    }
    async destroy() {
        if (!this.started || this.destroyed) {
            return;
        }
        logger.trace('Destroying ContentDeliveryManager');
        this.destroyed = true;
        await Promise.all(Array.from(this.streamParts.values()).map((streamPart) => streamPart.stop()));
        this.streamParts.clear();
        this.removeAllListeners();
        this.controlLayerNode = undefined;
        this.transport = undefined;
        this.connectionLocker = undefined;
    }
    broadcast(msg, streamPartDeliveryOptions) {
        const streamPartId = (0, utils_1.toStreamPartID)(msg.messageId.streamId, msg.messageId.streamPartition);
        logger.debug(`Broadcasting to stream part ${streamPartId}`);
        this.joinStreamPart(streamPartId, streamPartDeliveryOptions);
        this.streamParts.get(streamPartId).broadcast(msg);
        if (msg.body.oneofKind === 'contentMessage') {
            this.metrics.broadcastMessagesPerSecond.record(1);
            this.metrics.broadcastBytesPerSecond.record(msg.body.contentMessage.content.length);
        }
    }
    async leaveStreamPart(streamPartId) {
        const streamPart = this.streamParts.get(streamPartId);
        if (streamPart) {
            await streamPart.stop();
            this.streamParts.delete(streamPartId);
        }
    }
    joinStreamPart(streamPartId, streamPartDeliveryOptions) {
        let streamPart = this.streamParts.get(streamPartId);
        if (streamPart !== undefined) {
            return;
        }
        logger.debug(`Join stream part ${streamPartId}`);
        const discoveryLayerNode = this.createDiscoveryLayerNode(streamPartId, this.knownStreamPartEntryPoints.get(streamPartId) ?? []);
        const peerDescriptorStoreManager = new PeerDescriptorStoreManager_1.PeerDescriptorStoreManager({
            key: (0, exports.streamPartIdToDataKey)(streamPartId),
            localPeerDescriptor: this.getPeerDescriptor(),
            fetchDataFromDht: (key) => this.controlLayerNode.fetchDataFromDht(key),
            storeDataToDht: (key, data) => this.controlLayerNode.storeDataToDht(key, data),
            deleteDataFromDht: async (key, waitForCompletion) => this.controlLayerNode.deleteDataFromDht(key, waitForCompletion)
        });
        const networkSplitAvoidance = new StreamPartNetworkSplitAvoidance_1.StreamPartNetworkSplitAvoidance({
            discoveryLayerNode,
            discoverEntryPoints: async () => peerDescriptorStoreManager.fetchNodes()
        });
        const node = this.createContentDeliveryLayerNode(streamPartId, discoveryLayerNode, () => peerDescriptorStoreManager.isLocalNodeStored(), streamPartDeliveryOptions);
        const streamPartReconnect = new StreamPartReconnect_1.StreamPartReconnect(discoveryLayerNode, peerDescriptorStoreManager);
        streamPart = {
            proxied: false,
            discoveryLayerNode,
            node,
            networkSplitAvoidance,
            broadcast: (msg) => node.broadcast(msg),
            stop: async () => {
                streamPartReconnect.destroy();
                networkSplitAvoidance.destroy();
                await peerDescriptorStoreManager.destroy();
                node.stop();
                await discoveryLayerNode.stop();
            },
            getDiagnosticInfo: () => node.getDiagnosticInfo()
        };
        this.streamParts.set(streamPartId, streamPart);
        node.on('message', (message) => {
            this.emit('newMessage', message);
        });
        const handleEntryPointLeave = async () => {
            if (this.destroyed || peerDescriptorStoreManager.isLocalNodeStored() || this.knownStreamPartEntryPoints.has(streamPartId)) {
                return;
            }
            const entryPoints = await peerDescriptorStoreManager.fetchNodes();
            if (entryPoints.length < PeerDescriptorStoreManager_1.MAX_NODE_COUNT) {
                await peerDescriptorStoreManager.storeAndKeepLocalNode();
            }
        };
        discoveryLayerNode.on('manualRejoinRequired', async () => {
            if (!streamPartReconnect.isRunning() && !networkSplitAvoidance.isRunning()) {
                logger.debug('Manual rejoin required for stream part', { streamPartId });
                await streamPartReconnect.reconnect();
            }
        });
        node.on('entryPointLeaveDetected', () => handleEntryPointLeave());
        setImmediate(async () => {
            try {
                await this.startLayersAndJoinDht(streamPartId, peerDescriptorStoreManager);
            }
            catch (err) {
                logger.warn(`Failed to join to stream part ${streamPartId}`, { err });
            }
        });
    }
    async startLayersAndJoinDht(streamPartId, peerDescriptorStoreManager) {
        logger.debug(`Start layers and join DHT for stream part ${streamPartId}`);
        const streamPart = this.streamParts.get(streamPartId);
        if ((streamPart === undefined) || streamPart.proxied) {
            // leaveStreamPart has been called (or leaveStreamPart called, and then setProxies called)
            return;
        }
        if (this.transport.isPrivateClientMode()) {
            await this.transport.disablePrivateClientMode();
        }
        await streamPart.discoveryLayerNode.start();
        await streamPart.node.start();
        const knownEntryPoints = this.knownStreamPartEntryPoints.get(streamPartId);
        if (knownEntryPoints !== undefined) {
            await Promise.all([
                streamPart.discoveryLayerNode.joinDht(knownEntryPoints),
                streamPart.discoveryLayerNode.joinRing()
            ]);
        }
        else {
            const entryPoints = await peerDescriptorStoreManager.fetchNodes();
            await Promise.all([
                streamPart.discoveryLayerNode.joinDht((0, sampleSize_1.default)(entryPoints, StreamPartNetworkSplitAvoidance_1.MIN_NEIGHBOR_COUNT)),
                streamPart.discoveryLayerNode.joinRing()
            ]);
            if (entryPoints.length < PeerDescriptorStoreManager_1.MAX_NODE_COUNT) {
                await peerDescriptorStoreManager.storeAndKeepLocalNode();
                if (streamPart.discoveryLayerNode.getNeighborCount() < StreamPartNetworkSplitAvoidance_1.MIN_NEIGHBOR_COUNT) {
                    setImmediate(() => streamPart.networkSplitAvoidance.avoidNetworkSplit());
                }
            }
        }
    }
    createDiscoveryLayerNode(streamPartId, entryPoints) {
        return new dht_1.DhtNode({
            transport: this.controlLayerNode,
            connectionsView: this.controlLayerNode.getConnectionsView(),
            serviceId: 'layer1::' + streamPartId,
            peerDescriptor: this.controlLayerNode.getLocalPeerDescriptor(),
            entryPoints,
            numberOfNodesPerKBucket: 4, // TODO use options option or named constant?
            rpcRequestTimeout: dht_1.EXISTING_CONNECTION_TIMEOUT,
            dhtJoinTimeout: 20000, // TODO use options option or named constant?
            periodicallyPingNeighbors: true,
            periodicallyPingRingContacts: true,
            neighborPingLimit: 16
        });
    }
    createContentDeliveryLayerNode(streamPartId, discoveryLayerNode, isLocalNodeEntryPoint, streamPartDeliveryOptions) {
        return (0, createContentDeliveryLayerNode_1.createContentDeliveryLayerNode)({
            streamPartId,
            transport: this.transport,
            discoveryLayerNode,
            connectionLocker: this.connectionLocker,
            localPeerDescriptor: this.controlLayerNode.getLocalPeerDescriptor(),
            minPropagationTargets: this.options.streamPartitionMinPropagationTargets,
            neighborTargetCount: this.options.streamPartitionNeighborTargetCount,
            maxPropagationBufferSize: this.options.streamPartitionMaxPropagationBufferSize,
            acceptProxyConnections: this.options.acceptProxyConnections,
            rpcRequestTimeout: this.options.rpcRequestTimeout,
            neighborUpdateInterval: this.options.neighborUpdateInterval,
            isLocalNodeEntryPoint,
            doNotBufferWhileConnecting: this.options.doNotBufferWhileConnecting,
            plumTreeOptimisation: streamPartDeliveryOptions?.plumTreeOptimisation
        });
    }
    async setProxies(streamPartId, nodes, direction, userId, connectionCount) {
        // TODO explicit default value for "acceptProxyConnections" or make it required
        if (this.options.acceptProxyConnections) {
            throw new Error('cannot set proxies when acceptProxyConnections=true');
        }
        const enable = (nodes.length > 0) && ((connectionCount === undefined) || (connectionCount > 0));
        if (enable) {
            let client;
            const alreadyProxied = this.isProxiedStreamPart(streamPartId);
            if (alreadyProxied) {
                client = this.streamParts.get(streamPartId).client;
            }
            else {
                client = this.createProxyClient(streamPartId);
                this.streamParts.set(streamPartId, {
                    proxied: true,
                    client,
                    broadcast: (msg) => client.broadcast(msg),
                    stop: async () => client.stop(),
                    getDiagnosticInfo: () => client.getDiagnosticInfo()
                });
                client.on('message', (message) => {
                    this.emit('newMessage', message);
                });
                if (Array.from(this.streamParts.values()).every((streamPart) => streamPart.proxied)) {
                    await this.transport.enablePrivateClientMode();
                }
                await client.start();
            }
            await client.setProxies(nodes, direction, userId, connectionCount);
        }
        else {
            await this.streamParts.get(streamPartId)?.stop();
            this.streamParts.delete(streamPartId);
        }
    }
    createProxyClient(streamPartId) {
        return new ProxyClient_1.ProxyClient({
            transport: this.transport,
            localPeerDescriptor: this.controlLayerNode.getLocalPeerDescriptor(),
            streamPartId,
            connectionLocker: this.connectionLocker,
            minPropagationTargets: this.options.streamPartitionMinPropagationTargets ?? Propagation_1.DEFAULT_MIN_PROPAGATION_TARGETS,
            maxPropagationBufferSize: this.options.streamPartitionMaxPropagationBufferSize ?? Propagation_1.DEFAULT_MAX_PROPAGATION_BUFFER_SIZE,
            propagationBufferTtl: Propagation_1.DEFAULT_PROPAGATION_BUFFER_TTL
        });
    }
    async inspect(peerDescriptor, streamPartId) {
        const streamPart = this.streamParts.get(streamPartId);
        if ((streamPart !== undefined) && !streamPart.proxied) {
            return streamPart.node.inspect(peerDescriptor);
        }
        return false;
    }
    // TODO inline this method?
    getNodeInfo() {
        const streamParts = Array.from(this.streamParts.entries()).filter(([_, node]) => node.proxied === false);
        return streamParts.map(([streamPartId]) => {
            const stream = this.streamParts.get(streamPartId);
            return {
                id: streamPartId,
                controlLayerNeighbors: stream.discoveryLayerNode.getNeighbors(),
                deprecatedContentDeliveryLayerNeighbors: [],
                contentDeliveryLayerNeighbors: stream.node.getInfos()
            };
        });
    }
    setStreamPartEntryPoints(streamPartId, entryPoints) {
        this.knownStreamPartEntryPoints.set(streamPartId, entryPoints);
    }
    isProxiedStreamPart(streamPartId, direction) {
        const streamPart = this.streamParts.get(streamPartId);
        return (streamPart !== undefined)
            && streamPart.proxied
            && ((direction === undefined) || (streamPart.client.getDirection() === direction));
    }
    getStreamPartDelivery(streamPartId) {
        return this.streamParts.get(streamPartId);
    }
    hasStreamPart(streamPartId) {
        return this.streamParts.has(streamPartId);
    }
    getPeerDescriptor() {
        return this.controlLayerNode.getLocalPeerDescriptor();
    }
    getNodeId() {
        return (0, dht_1.toNodeId)(this.controlLayerNode.getLocalPeerDescriptor());
    }
    getNeighbors(streamPartId) {
        const streamPart = this.streamParts.get(streamPartId);
        return (streamPart !== undefined) && (streamPart.proxied === false)
            ? streamPart.node.getNeighbors().map((n) => (0, dht_1.toNodeId)(n))
            : [];
    }
    getStreamParts() {
        return Array.from(this.streamParts.keys()).map((id) => utils_1.StreamPartIDUtils.parse(id));
    }
    getDiagnosticInfo() {
        return {
            streamParts: this.getStreamParts().map((id) => {
                return {
                    id,
                    info: this.getStreamPartDelivery(id).getDiagnosticInfo()
                };
            })
        };
    }
}
exports.ContentDeliveryManager = ContentDeliveryManager;
//# sourceMappingURL=ContentDeliveryManager.js.map